import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface StatsChartsProps {
  stats: {
    users: {
      byPermission: Array<{ permission: string; count: number }>;
    };
    growth: {
      users: Array<{ date: string; count: number }>;
      active: Array<{ date: string; count: number }>;
    };
    prompts: {
      stats: Array<{
        prompt_id: string;
        total_actions: number;
        views: number;
        copies: number;
        favorites: number;
      }>;
    };
  };
}

const COLORS = ['#000000', '#666666', '#999999', '#cccccc'];

export function StatsCharts({ stats }: StatsChartsProps) {
  if (!stats) {
    return null;
  }

  // 用户权限分布数据
  const permissionData = (stats.users?.byPermission || []).map(item => ({
    name: item.permission,
    value: item.count,
  }));

  // 用户增长数据
  const growthData = (stats.growth?.users || []).map((item, index) => ({
    date: new Date(item.date).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
    新增用户: item.count,
    活跃用户: stats.growth?.active?.[index]?.count || 0,
  }));

  // Prompt 使用数据 (Top 10)
  const promptData = (stats.prompts?.stats || []).slice(0, 10).map(item => ({
    id: item.prompt_id.substring(0, 15) + '...',
    查看: item.views,
    复制: item.copies,
    收藏: item.favorites,
  }));

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* 用户权限分布 */}
      <Card>
        <CardHeader>
          <CardTitle>用户权限分布</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={permissionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {permissionData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* 用户增长趋势 */}
      <Card>
        <CardHeader>
          <CardTitle>用户增长趋势</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="新增用户" stroke="#000000" strokeWidth={2} />
              <Line type="monotone" dataKey="活跃用户" stroke="#666666" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Prompt 使用统计 */}
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Prompt 使用统计 (Top 10)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={promptData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="id" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="查看" fill="#000000" />
              <Bar dataKey="复制" fill="#666666" />
              <Bar dataKey="收藏" fill="#999999" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

